import { ChevronLeft, ChevronRight } from "@tamagui/lucide-icons";
import React, { useRef, useState } from "react";
import { Dimensions, Image } from "react-native";
import Carousel, { ICarouselInstance } from "react-native-reanimated-carousel";
import { XStack, YStack, getTokenValue, useMedia } from "tamagui";

import { IconButton, Text } from "@/components";
import type { CarouselItem as CarouselItemType } from "@/hooks/suggestions";

const { width: screenWidth } = Dimensions.get("window");

const ITEM_WIDTH = 282;
const ITEM_HEIGHT = 360;
const ITEM_HEIGHT_NO_DESCRIPTION = 314;
const IMAGE_HEIGHT = 188;

interface CarouselItemProps {
  item: CarouselItemType;
  index: number;
  cardColor: string;
}

const CarouselItem: React.FC<CarouselItemProps> = ({
  item: { image, title, description },
  index,
  cardColor,
}) => {
  return (
    <YStack
      width={ITEM_WIDTH}
      height={description ? ITEM_HEIGHT : ITEM_HEIGHT_NO_DESCRIPTION}
      overflow="hidden"
      borderRadius="$200"
      backgroundColor={cardColor}
    >
      <Image
        source={image}
        style={{ width: ITEM_WIDTH, height: IMAGE_HEIGHT, resizeMode: "cover" }}
      />
      <YStack
        paddingHorizontal="$300"
        paddingTop="$200"
        paddingBottom="$300"
        gap="$100"
      >
        <Text size="$lg" medium>
          {title}
        </Text>
        {description && <Text>{description}</Text>}
      </YStack>
    </YStack>
  );
};

export default function ClickableCarousel({
  items,
  cardColor,
}: {
  items: CarouselItemType[];
  cardColor: string;
}) {
  const { xl } = useMedia();
  const carouselRef = useRef<ICarouselInstance>(null);

  const [carouselCurrentIdx, setCarouselCurrentIdx] = useState(0);

  return (
    <YStack
      alignSelf="center"
      width="100%"
      maxWidth={
        // Static image width * number of images + (padding * number of images - first and last image padding)
        ITEM_WIDTH * items.length + (getTokenValue("$200") * items.length - 2)
      }
      overflow="visible"
    >
      <Carousel
        onConfigurePanGesture={(gesture) => {
          // Allow vertical scrolling of the containing ScrollView
          gesture.config.touchAction = "pan-y";
          gesture.activeOffsetX([-10, 10]);
        }}
        loop={false}
        data={items}
        height={ITEM_HEIGHT}
        width={ITEM_WIDTH + getTokenValue("$200")}
        style={{
          width: screenWidth,
          overflow: "visible",
        }}
        containerStyle={{
          paddingLeft: getTokenValue("$200"),
        }}
        renderItem={({ item, index }) => (
          <CarouselItem item={item} index={index} cardColor={cardColor} />
        )}
        snapEnabled={true}
        overscrollEnabled={false}
        scrollAnimationDuration={500}
        enabled={!xl}
        onSnapToItem={(idx) => setCarouselCurrentIdx(idx)}
        ref={carouselRef}
      />
      {!xl && (
        <XStack
          alignSelf="flex-end"
          marginTop="$350"
          gap="$200"
          paddingRight="$250"
        >
          <IconButton
            disabled={carouselCurrentIdx === 0}
            Icon={ChevronLeft}
            aria-label="Previous"
            onPress={() => carouselRef.current?.prev()}
          />
          <IconButton
            disabled={carouselCurrentIdx === items.length - 1}
            Icon={ChevronRight}
            aria-label="Next"
            onPress={() => carouselRef.current?.next()}
          />
        </XStack>
      )}
    </YStack>
  );
}
